import React, { useEffect, useState } from "react";
import Header from "./Header";
import api from "../api/api";
import * as XLSX from "xlsx";

export default function OrderandPayment() {

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTomorrowDate());
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");

  const [columnVisibility, setColumnVisibility] = useState({});
  const [showCustomize, setShowCustomize] = useState(false);
  const [excludedFromCustomize, setExcludedFromCustomize] = useState([]);

  // Order Item Popup State
  const [showOrderItems, setShowOrderItems] = useState(false);
  const [orderItems, setOrderItems] = useState([]);
  const [orderItemsLoading, setOrderItemsLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Filter counts
  const [filterCounts, setFilterCounts] = useState({
    All: 0,
    COD: 0,
    Paid: 0,
    Failed: 0,
    Takeaway: 0,
    Delivery: 0,
  });

  // Filter amounts (sum of OrderValue)
  const [filterAmounts, setFilterAmounts] = useState({
    All: 0,
    COD: 0,
    Paid: 0,
    Failed: 0,
    Takeaway: 0,
    Delivery: 0,
  });

  // Fetch order summary data
  const fetchOrderSummary = async () => {
  try {
    setLoading(true);
    const response = await api.GetOrderSummary(fromDate, toDate);
    const data = response?.GetOrderSummaryjs?.Table || [];

    setOrders(data);
    setFilteredOrders(data);
    calculateFilterCounts(data);

    // Initialize column visibility dynamically
    if (data.length > 0) {
      const columns = {};
      const hiddenFromAll = ["Oid", "Vid"]; // Columns to hide from both table and customize
      const hiddenFromCustomize = ["Vcode", "Customer", "Division", "OrderDate"]; // Columns to show in table but not in customize
      Object.keys(data[0]).forEach((key) => {
        if (!hiddenFromAll.includes(key)) {
          columns[key] = true; // show in table
        }
      });
      setColumnVisibility(columns);
      setExcludedFromCustomize(hiddenFromCustomize);
    }

  } catch (error) {
    console.error("Error fetching order summary:", error);
  } finally {
    setLoading(false);
  }
};

  // Calculate filter counts and amounts
  const calculateFilterCounts = (data) => {
    const counts = {
      All: data.length,
      COD: data.filter((o) => o.Paymode?.toLowerCase() === "cod").length,
      Paid: data.filter((o) => o.Paymode?.toLowerCase() === "upi" || o.Paymode?.toLowerCase() === "paid").length,
      Failed: data.filter((o) => o.Paymode?.toLowerCase() === "pending").length,
      Takeaway: data.filter((o) => Number(o.IsTakeAway) === 1).length,
      Delivery: data.filter((o) => Number(o.IsTakeAway) === 0).length,
    };
    setFilterCounts(counts);

    // Calculate sum of OrderValue for each filter
    const amounts = {
      All: data.reduce((sum, o) => sum + Number(o.OrderValue || 0), 0),
      COD: data.filter((o) => o.Paymode?.toLowerCase() === "cod").reduce((sum, o) => sum + Number(o.OrderValue || 0), 0),
      Paid: data.filter((o) => o.Paymode?.toLowerCase() === "upi" || o.Paymode?.toLowerCase() === "paid").reduce((sum, o) => sum + Number(o.OrderValue || 0), 0),
      Failed: data.filter((o) => o.Paymode?.toLowerCase() === "pending").reduce((sum, o) => sum + Number(o.OrderValue || 0), 0),
      Takeaway: data.filter((o) => Number(o.IsTakeAway) === 1).reduce((sum, o) => sum + Number(o.OrderValue || 0), 0),
      Delivery: data.filter((o) => Number(o.IsTakeAway) === 0).reduce((sum, o) => sum + Number(o.OrderValue || 0), 0),
    };
    setFilterAmounts(amounts);
  };

  // Initial fetch on component mount
  useEffect(() => {
    fetchOrderSummary();
  }, []);

  // Apply filters and search
  useEffect(() => {
    let result = [...orders];

    // Apply category filter
    if (activeFilter !== "All") {
      if (activeFilter === "COD") {
        result = result.filter((o) => o.Paymode?.toLowerCase() === "cod");
      } else if (activeFilter === "Paid") {
        result = result.filter((o) => o.Paymode?.toLowerCase() === "upi" || o.Paymode?.toLowerCase() === "paid");
      } else if (activeFilter === "Failed") {
        result = result.filter((o) => o.Paymode?.toLowerCase() === "pending");
      } else if (activeFilter === "Takeaway") {
        result = result.filter((o) => Number(o.IsTakeAway) === 1);
      } else if (activeFilter === "Delivery") {
        result = result.filter((o) => Number(o.IsTakeAway) === 0);
      }
    }

    // Apply search filter
   if (searchQuery) {
  const query = searchQuery.toLowerCase();

  result = result.filter((order) =>
    Object.values(order).some((value) => {
      if (value === null || value === undefined) return false;

      // Convert everything to string and compare
      return String(value).toLowerCase().includes(query);
    })
  );
}

    setFilteredOrders(result);
  }, [activeFilter, searchQuery, orders]);

  // Handle logout
  const handleLogout = () => {
    sessionStorage.removeItem("isLoggedIn");
    sessionStorage.removeItem("username");
    sessionStorage.removeItem("lastVisited");
    window.location.href = "/";
  };

  // Handle back navigation
  const handleBack = () => window.history.back();

  // Export to Excel
  const exportToExcel = () => {
    const exportData = filteredOrders.map((order, index) => ({
      SlNo: index + 1,
      Vcode: order.Vcode,
      Customer: order.Vname,
      Division: order.Division,
      OrderDate: order.OrderDate
        ? new Date(order.OrderDate).toLocaleDateString("en-GB")
        : "-",
      DeliveryDate1: order.DeliveryDate1
        ? new Date(order.DeliveryDate1).toLocaleDateString("en-GB")
        : "-",
      DeliveryDate2: order.DeliveryDate2
        ? new Date(order.DeliveryDate2).toLocaleDateString("en-GB")
        : "-",
      DayCount: order.DayCount,
      LineItemCount: order.LineItemCount,
      LineItemQtySum: order.LineItemQtySum,
      OrderValue: order.OrderValue,
      Mode: order.Paymode,
      TxnNo: order.TxnNo,
      Type: Number(order.IsTakeAway) === 1 ? "Takeaway" : "Delivery",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "OrderPayments");
    XLSX.writeFile(workbook, `OrderPayments_${fromDate}_to_${toDate}.xlsx`);
  };

  // Get status class based on mode
  const getStatusClass = (mode) => {
    const modeLower = (mode || "").toLowerCase();
    switch (modeLower) {
      case "upi":
        return "text-blue-600 font-semibold";
      case "cod":
        return "text-orange-500 font-semibold";
      case "paid":
        return "text-green-600 font-semibold";
      case "pending":
        return "text-red-500 font-semibold";
      default:
        return "text-gray-600";
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-GB");
    } catch {
      return dateStr;
    }
  };

  // Fetch order items when LineItemCount is clicked
  const fetchOrderItems = async (order) => {
    try {
      setOrderItemsLoading(true);
      setSelectedOrder(order);
      setShowOrderItems(true);
      const response = await api.GetOrderItem(order.Oid);
      const data = response?.GetOrderItemjs?.Table || [];
      setOrderItems(data);
    } catch (error) {
      console.error("Error fetching order items:", error);
      setOrderItems([]);
    } finally {
      setOrderItemsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onLogout={handleLogout} />
      <main className="p-6 md:ml-64">
        <div className="p-4 mx-auto mt-3 bg-white shadow-2xl sm:p-6 rounded-2xl max-w-8xl">
          {/* Header Section */}
          <div className="flex flex-col justify-between gap-3 pb-3 mb-4 border-b sm:flex-row sm:items-center">
            <button
              onClick={handleBack}
              className="inline-flex items-center self-start w-auto gap-1 px-3 py-1 text-sm font-semibold text-gray-800 transition bg-gray-200 rounded-full shadow hover:bg-gray-300 sm:text-base"
            >
              ← Back
            </button>

            <h1 className="flex-1 text-2xl font-bold text-center text-orange-600 sm:text-3xl">
              🧾 Order & Payments
            </h1>

            <button
              onClick={exportToExcel}
              className="inline-flex self-start w-auto px-2 py-1 font-semibold text-white transition bg-blue-500 rounded-full shadow hover:bg-blue-700"
            >
              📄 Export Excel
            </button>
          </div>

          {/* Date Filters */}
          <div className="flex flex-col gap-4 p-4 mb-4 sm:flex-row sm:items-center bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">From:</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">To:</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
            <button
              onClick={fetchOrderSummary}
              disabled={loading}
              className="px-6 py-2 font-semibold text-white transition bg-orange-500 rounded-full shadow-md hover:bg-orange-600 disabled:bg-orange-300"
            >
              {loading ? "Loading..." : "🔍 Search"}
            </button>
           
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap gap-2 mb-4">
            {["All", "COD", "Paid", "Failed", "Takeaway", "Delivery"].map(
              (filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-4 py-2 rounded-full font-medium transition shadow-sm ${
                    activeFilter === filter
                      ? "bg-orange-500 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {filter}
                  <span
                    className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      activeFilter === filter
                        ? "bg-white text-orange-600"
                        : "bg-orange-100 text-orange-600"
                    }`}
                  >
                    {filterCounts[filter]}
                  </span>
                  <span
                    className={`ml-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                      activeFilter === filter
                        ? "bg-orange-600 text-white"
                        : "bg-green-100 text-green-600"
                    }`}
                  >
                    ₹{filterAmounts[filter].toLocaleString()}
                  </span>
                </button>
              )
            )}
          </div>

          {/* Search Box */}
          <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
            <button
              onClick={() => setShowCustomize(true)}
              className="flex items-center gap-2 px-4 py-2 font-medium text-white transition bg-gray-700 rounded-full shadow hover:bg-gray-800"
            >
              ⚙ Customize
            </button>
            <input
              type="text"
              placeholder="🔍 Search Orders & Payments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-full shadow-sm sm:w-1/3 focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-4 border-t-4 border-orange-500 rounded-full animate-spin"></div>
                <p className="text-gray-600">Loading order data...</p>
              </div>
            </div>
          ) : (
            /* Table */
            <div className="overflow-x-auto border border-gray-200 shadow-inner rounded-xl">
              <table className="min-w-full text-sm text-gray-700">
             <thead className="sticky top-0 text-white bg-gradient-to-r from-orange-500 to-orange-500">
  <tr>
    <th className="px-3 py-2">Sl No</th>

    {orders.length > 0 &&
      Object.keys(orders[0])
        .filter((key) => columnVisibility[key])
        .map((key) => (
          <th key={key} className="px-3 py-2 whitespace-nowrap">
            {key}
          </th>
        ))}
  </tr>
</thead>

                <tbody>
  {filteredOrders.length > 0 ? (
    filteredOrders.map((order, index) => (
      <tr
        key={index}
        className={`transition hover:bg-orange-50 ${
          index % 2 === 0 ? "bg-white" : "bg-gray-50"
        }`}
      >
        {/* Serial Number */}
        <td className="px-3 py-2">{index + 1}</td>

        {/* Dynamic Columns */}
        {Object.keys(order)
          .filter((key) => columnVisibility[key])
          .map((key) => (
            <td key={key} className="px-3 py-2 whitespace-nowrap">
              {key === "IsTakeAway" ? (
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    Number(order[key]) === 1
                      ? "bg-purple-100 text-purple-700"
                      : "bg-teal-100 text-teal-700"
                  }`}
                >
                  {Number(order[key]) === 1
                    ? "Takeaway"
                    : "Delivery"}
                </span>
              ) : key === "LineItemCount" ? (
                <button
                  onClick={() => fetchOrderItems(order)}
                  className="px-3 py-1 font-semibold text-white transition bg-orange-500 rounded-full shadow-md bg-gradient-to-r hover:bg-orange-600 disabled:bg-orange-300"
                  title="Click to view order items"
                >
                  {order[key]}
                </button>
              ) : key.includes("Date") ? (
                formatDate(order[key])
              ) : (
                order[key] ?? "-"
              )}
            </td>
          ))}
      </tr>
    ))
  ) : (
    <tr>
      <td colSpan="100%" className="py-8 text-center text-gray-500">
        No orders found
      </td>
    </tr>
  )}
</tbody>
              </table>
              
             
            </div>
            
          )}
{showCustomize && (
  <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
    <div className="w-full max-w-md p-6 bg-white shadow-2xl rounded-xl">
      <h2 className="mb-4 text-lg font-bold text-gray-800">
        Customize Columns
      </h2>

      <div className="space-y-2 overflow-y-auto max-h-80">
        {Object.keys(columnVisibility)
          .filter((col) => !excludedFromCustomize.includes(col))
          .map((col) => (
          <label
            key={col}
            className="flex items-center justify-between px-3 py-2 rounded hover:bg-gray-100"
          >
            <span>{col}</span>
            <input
              type="checkbox"
              checked={columnVisibility[col]}
              onChange={() =>
                setColumnVisibility((prev) => ({
                  ...prev,
                  [col]: !prev[col],
                }))
              }
              className="w-4 h-4"
            />
          </label>
        ))}
      </div>

      <div className="flex justify-end mt-4">
        <button
          onClick={() => setShowCustomize(false)}
          className="px-4 py-2 text-white bg-orange-500 rounded-lg hover:bg-orange-600"
        >
          Done
        </button>
      </div>
    </div>
  </div>
)}

{/* Order Items Popup Modal */}
{showOrderItems && (
  <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
    <div className="w-full max-w-4xl max-h-[90vh] p-6 bg-white shadow-2xl rounded-xl overflow-hidden flex flex-col">
      {/* Modal Header */}
      <div className="flex items-center justify-between pb-3 mb-4 border-b">
        <div>
          <h2 className="text-lg font-bold text-gray-800">
            Order Items Details
          </h2>
          {selectedOrder && (
            <div className="space-y-1 text-sm text-gray-500">
              <p>
                Order ID: <span className="font-semibold text-orange-600">{selectedOrder.Oid}</span>
                {" | "}
                Customer: <span className="font-semibold">{orderItems.length > 0 ? (orderItems[0].Vname || selectedOrder.Customer || selectedOrder.Vname) : (selectedOrder.Customer || selectedOrder.Vname)}</span>
                {" | "}
                <span className="text-gray-400">{selectedOrder.Vcode}</span>
              </p>
              <p>
                Paymode:{" "}
                <span className={`font-semibold px-2 py-0.5 rounded-full text-xs ${
                  (selectedOrder.Paymode || "").toUpperCase() === "COD" ? "bg-orange-100 text-orange-700" :
                  (selectedOrder.Paymode || "").toUpperCase() === "UPI" || (selectedOrder.Paymode || "").toUpperCase() === "PAID" ? "bg-green-100 text-green-700" :
                  (selectedOrder.Paymode || "").toUpperCase() === "PENDING" ? "bg-red-100 text-red-700" :
                  "bg-gray-100 text-gray-700"
                }`}>
                  {(selectedOrder.Paymode || "N/A").toUpperCase()}
                </span>
              </p>
            </div>
          )}
        </div>
        {/* Order Total at Top Right */}
        <div className="flex flex-col items-end gap-2">
          {orderItems.length > 0 && (
            <div className="px-4 py-2 border border-green-200 rounded-lg bg-gradient-to-r from-green-50 to-green-100">
              <span className="text-sm text-gray-600">Order Total: </span>
              <span className="text-lg font-bold text-green-600">
                ₹{orderItems.reduce((sum, item) => sum + (item.Amount || 0), 0).toFixed(2)}
              </span>
            </div>
          )}
          <button
            onClick={() => {
              setShowOrderItems(false);
              setOrderItems([]);
              setSelectedOrder(null);
            }}
            className="p-2 text-gray-400 transition rounded-full hover:bg-gray-100 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Loading State */}
      {orderItemsLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-10 h-10 mx-auto mb-4 border-t-4 border-blue-500 rounded-full animate-spin"></div>
            <p className="text-gray-600">Loading order items...</p>
          </div>
        </div>
      ) : orderItems.length > 0 ? (
        /* Order Items Table - Dynamic Columns */
        <div className="flex-1 overflow-x-auto">
          <table className="min-w-full text-sm text-gray-700">
            <thead className="sticky top-0 text-white bg-orange-500 rounded-full shadow-md bg-gradient-to-r hover:bg-orange-600 disabled:bg-orange-300">
              <tr>
                <th className="px-4 py-3 text-left">Sl No</th>
                {Object.keys(orderItems[0])
                  .filter((key) => !["OId", "Vid", "Itemid"].includes(key))
                  .map((key) => (
                    <th key={key} className="px-4 py-3 text-left whitespace-nowrap">
                      {key}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {orderItems.map((item, index) => (
                <tr
                  key={index}
                  className={`transition hover:bg-blue-50 ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                  }`}
                >
                  <td className="px-4 py-3">{index + 1}</td>
                  {Object.keys(item)
                    .filter((key) => !["OId", "Vid", "Itemid"].includes(key))
                    .map((key) => (
                      <td key={key} className="px-4 py-3 whitespace-nowrap">
                        {item[key] ?? "-"}
                      </td>
                    ))}
                </tr>
              ))}
            </tbody>
            {/* Table Footer with Totals */}
            <tfoot className="font-semibold bg-gradient-to-r from-gray-100 to-gray-200">
              {(() => {
                const visibleColumns = Object.keys(orderItems[0]).filter((key) => !["OId", "Vid", "Itemid"].includes(key));
                const qtyIndex = visibleColumns.indexOf("Qty");
                const totalColSpan = qtyIndex + 1; // Sl No + columns before Qty
                
                return (
                  <tr>
                    <td className="px-4 py-3 text-right" colSpan={totalColSpan}>
                      Total:
                    </td>
                    {visibleColumns.slice(qtyIndex).map((key) => (
                      <td key={key} className="px-4 py-3">
                        {key === "Qty" ? (
                          <span className="text-blue-600">
                            {orderItems.reduce((sum, item) => sum + (item.Qty || 0), 0)}
                          </span>
                        ) : key === "Amount" ? (
                          <span className="text-green-600">
                            Rs. {orderItems.reduce((sum, item) => sum + (item.Amount || 0), 0)}
                          </span>
                        ) : null}
                      </td>
                    ))}
                  </tr>
                );
              })()}
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="flex items-center justify-center py-12">
          <p className="text-gray-500">No order items found</p>
        </div>
      )}

      {/* Modal Footer */}
      <div className="flex justify-end pt-4 mt-4 border-t">
        <button
          onClick={() => {
            setShowOrderItems(false);
            setOrderItems([]);
            setSelectedOrder(null);
          }}
          className="px-6 py-2 font-medium text-white transition bg-orange-500 rounded-full shadow-md hover:bg-orange-600 disabled:bg-orange-300"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}
          {/* Summary Footer */}
          {!loading && filteredOrders.length > 0 && (
            <div className="p-4 mt-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600">
                    Total Orders:{" "}
                    <span className="font-bold text-orange-600">
                      {filteredOrders.length}
                    </span>
                  </span>
                  <span className="text-sm text-gray-600">
                    Total Order Value:{" "}
                    <span className="font-bold text-green-600">
                      ₹
                      {filteredOrders.reduce(
                        (sum, o) => sum + (o.OrderValue || 0),
                        0
                      )}
                    </span>
                  </span>
                  <span className="text-sm text-gray-600">
                    Total Qty:{" "}
                    <span className="font-bold text-blue-600">
                      {filteredOrders.reduce(
                        (sum, o) => sum + (o.LineItemQtySum || 0),
                        0
                      )}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

